import { z } from 'zod';
import { localizedTextSchema, optionalLocalizedTextSchema } from './localized';
import { contentUsesOnlyPublicMediaUrls } from '../case-content';
import { fieldTranslationMetadataSchema, translationReviewSchema } from './translation-review';

export const caseCategorySchema = z.enum(['incentive', 'event', 'luxury']);
export const contentStatusSchema = z.enum(['draft', 'in_review', 'changes_requested', 'published']);

const tiptapDocumentSchema = z
  .record(z.string(), z.unknown())
  .refine(contentUsesOnlyPublicMediaUrls, { message: 'Rich content images must use public media URLs.' });
const translationEntrySchema = z.object({
  block: tiptapDocumentSchema,
  sourceHash: z.string().min(1),
  origin: translationReviewSchema.shape.origin,
  reviewedHash: translationReviewSchema.shape.reviewedHash,
});
const optionalCaseContentSchema = z
  .object({
    master: tiptapDocumentSchema,
    sourceLanguage: z.enum(['nl', 'fr', 'en']),
    fieldMetadata: fieldTranslationMetadataSchema.optional(),
    translations: z
      .object({
        nl: z.record(z.string(), translationEntrySchema).optional(),
        fr: z.record(z.string(), translationEntrySchema).optional(),
        en: z.record(z.string(), translationEntrySchema).optional(),
      })
      .optional(),
  })
  .optional();

export const caseSchema = z.object({
  id: z.string().uuid().optional(),
  category: caseCategorySchema,
  slug: z
    .string()
    .min(1, 'Slug is verplicht')
    .max(160)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Gebruik kleine letters, cijfers en koppeltekens'),
  title: localizedTextSchema,
  tagline: optionalLocalizedTextSchema,
  subtitle: optionalLocalizedTextSchema,
  content: optionalCaseContentSchema,
  location: optionalLocalizedTextSchema,
  attendees: optionalLocalizedTextSchema,
  company: optionalLocalizedTextSchema,
  duration: optionalLocalizedTextSchema,
  status: contentStatusSchema.default('draft'),
  isFeatured: z.boolean().default(false),
  sortOrder: z.number().int().min(0).default(0),
  seoDescription: optionalLocalizedTextSchema,
  reviewNotes: z.string().max(4000, 'Reviewnotities mogen maximaal 4000 tekens bevatten').nullable().optional(),
  media: z
    .array(
      z.object({
        mediaAssetId: z.string().uuid(),
        role: z.enum(['hero', 'gallery', 'content']).default('gallery'),
        sortOrder: z.number().int().min(0).default(0),
        cropX: z.number().int().min(0).max(100).default(50),
      })
    )
    .default([]),
});

export const createCaseSchema = caseSchema.omit({ id: true });
export const updateCaseSchema = caseSchema.partial().omit({ id: true });

export type CaseFormData = z.infer<typeof createCaseSchema>;
